"use client";

import { MouseEvent, useCallback, useEffect, useState } from "react";
import { DocRenderer } from "react-doc-viewer";
import dynamic from "next/dynamic";
import cn from "classnames";

import AscImage from "../AscImage/AscImage";
import AscModal from "../AscModal/AscModal";
import styles from "./AscResourceDocCard.module.scss";
import AscVideoPlayer from "../AscVideoPlayer/AscVideoPlayer";
import { determineFileTypeFromExtension } from "../../lib/helpers";

import { CaptionTranslation, translationLabel } from "@ascendnow/shared";
const AscPDFViewer = dynamic(() => import("../AscPDFViewer/AscPDFViewer"), { ssr: false });

// Dynamically load react-doc-viewer client-side without SSR since it depends on
// the window object
const DocViewer = dynamic(() => import("react-doc-viewer"), {
  ssr: false,
});

interface IDocReader {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  src: string;
  captions: CaptionTranslation[];
}

export const DocReader = ({
  visible,
  setVisible,
  src,
  captions,
}: IDocReader) => {
  const [renderers, setRenderers] = useState<DocRenderer[]>([]);

  const srcType = src?.split(".").pop()?.toLocaleLowerCase();
  const isPdf = srcType?.split(".")?.pop()?.toLowerCase() === "pdf";
  const fileType = src
    ? determineFileTypeFromExtension(srcType ? srcType : "")
    : "";
  const fileName = src?.split("/").pop();

  // Dynamically load doc renderers to avoid SSR issues
  useEffect(() => {
    (async () => {
      const DocViewRenderers = (await import("react-doc-viewer"))
        .DocViewerRenderers;
      setRenderers([...DocViewRenderers]);
    })();
  }, []);

  const handleContextMenu = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <AscModal
      visible={visible}
      setVisible={setVisible}
      modalClassName={cn(
        styles.modal,
        fileType === "video" && styles.landscape
      )}
      withBackground={false}
      header={fileName}
      headerClassName={styles.modalHeader}
    >
      <div
        className={styles.docReader}
        onContextMenu={handleContextMenu}
        tabIndex={1}
      >
        {fileType === "image" && (
          <AscImage
            src={src}
            alt="doc-image"
            className={styles.image}
            tabIndex={0}
          />
        )}
        {fileType === "video" && (
          <AscVideoPlayer
            url={src}
            className={styles.video}
            captions={
              captions && captions.length > 0
                ? captions?.map((el: CaptionTranslation) => ({
                    label:
                      translationLabel[
                        el?.language as keyof typeof translationLabel
                      ],
                    srclang: el?.language,
                    src: el?.url,
                  }))
                : []
            }
          />
        )}
        {visible && !isPdf && (fileType === "docs" || fileType === "other") && (
          <DocViewer
            config={{
              header: {
                retainURLParams: false,
                disableFileName: true,
                disableHeader: true,
              },
            }}
            className={styles.docViewer}
            documents={[{ uri: src }]}
            pluginRenderers={renderers}
          />
        )}
        {isPdf && (
          <AscPDFViewer src={src} />
        )}
      </div>
    </AscModal>
  );
};
